/*
 * TTJ global pincode popup.
 *
 * Shown on any storefront page when there is no valid saved pincode (session cookie ttj_delivery_pincode).
 *
 * WHEN it opens (no timers anywhere):
 *  - Non-homepage pages (collection, product, etc.): immediately when the page loads.
 *  - Homepage (main[data-template="index"]): NOT on load. It opens once, when the visitor has scrolled and the
 *    "Most Loved Pieces" section reaches the viewport (IntersectionObserver). Navigating to any other page then
 *    opens it on that page (previous bullet).
 *  - Never when a valid pincode is saved, and never again in the same session after the visitor used
 *    "Continue Shopping for Jewellery" (sessionStorage flag ttj_pincode_popup_dismissed).
 *  - Only one popup can exist: guarded by openedOnce / root.hidden and a per-page init flag.
 * Uses the shared delivery core (assets/ttj-delivery-core.js) for validation, the existing API call and the
 * cookie, so there is one API integration.
 *
 *  - Exactly 6 digits are required before anything is sent; the API (not the digit count) decides availability.
 *  - Spinner in the Check button while the request runs; duplicate submits are ignored.
 *  - No close / cross / Escape / backdrop dismissal. The popup closes only after an API answer:
 *      available   -> cookie saved, the dialog switches to a thank-you state (form replaced by the message and a
 *                     "Continue Browsing" button). The popup and dimmed backdrop stay until that button is clicked.
 *      unavailable -> cookie saved, the amber "not available" message is shown and a "Continue browsing"
 *                     button appears (so a visitor outside the delivery area is not locked out). Typing a
 *                     different pincode checks again.
 *      failure     -> error message, popup stays, visitor can retry.
 *  - After the answer the popup announces `ttj:delivery-result`; a product-page checker shows it without a
 *    second API request.
 *  - While open the page behind is dimmed by a fixed backdrop and is inert (no clicks, no keyboard focus), but it
 *    is NOT scroll-locked: the visitor can still scroll the page vertically. Keyboard focus stays in the dialog.
 */
(function () {
  'use strict';

  var core = window.TTJDelivery;
  var root = document.querySelector('[data-ttj-pincode-modal]');
  if (!core || !root) return;
  if (core.getSavedPincode()) return; // valid pincode already saved this session: never show
  if (root.hasAttribute('data-ttj-pincode-init')) return; // never bind twice (duplicate script / re-execution)
  root.setAttribute('data-ttj-pincode-init', '');

  var DISMISSED_KEY = 'ttj_pincode_popup_dismissed';
  // Homepage trigger: the Most Loved Pieces section. Its wrapper id is "shopify-section-template--<id>__featured_collection";
  // the template-specific prefix changes between theme versions, the section key "featured_collection" does not
  // (the other homepage carousels use keys with a suffix, e.g. __featured_collection_qDpYki, so they do not match).
  var MOST_LOVED_SELECTOR = '#MainContent .shopify-section[id^="shopify-section-"][id$="__featured_collection"]';

  function wasDismissed() {
    try {
      return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function markDismissed() {
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch (error) {
      // storage blocked: the saved-pincode cookie still prevents re-showing in the normal flow
    }
  }

  if (wasDismissed()) return; // dismissed earlier this session: do not reopen

  var dialog = root.querySelector('.ttj-pincode-modal__dialog');
  var form = root.querySelector('.ttj-pincode-modal__form');
  var input = root.querySelector('.ttj-pincode-modal__input');
  var button = root.querySelector('.ttj-pincode-modal__button');
  var result = root.querySelector('.ttj-pincode-modal__result');
  var message = root.querySelector('.ttj-pincode-modal__message');
  var continueButton = root.querySelector('.ttj-pincode-modal__continue');
  var successBox = root.querySelector('.ttj-pincode-modal__success');
  var successButton = root.querySelector('.ttj-pincode-modal__continue-success');
  var defaultBlocks = root.querySelectorAll(
    '.ttj-pincode-modal__dialog > .ttj-pincode-modal__title, .ttj-pincode-modal__dialog > .ttj-pincode-modal__text, ' +
      '.ttj-pincode-modal__points, .ttj-pincode-modal__form, .ttj-pincode-modal__result'
  );
  if (!dialog || !form || !input || !button || !result || !message || !continueButton || !successBox || !successButton) return;

  var loading = false;
  var inerted = [];
  var openedOnce = false;

  function show(state, text) {
    message.textContent = text;
    result.classList.toggle('is-error', state === 'error');
    result.classList.toggle('is-unavailable', state === 'unavailable');
    result.hidden = false;
  }

  function clearMessage() {
    result.hidden = true;
    message.textContent = '';
    continueButton.hidden = true;
    input.setAttribute('aria-invalid', 'false');
  }

  // Replaces the pincode form with the confirmation message. Only called after the API said available === true.
  function showSuccess() {
    Array.prototype.forEach.call(defaultBlocks, function (el) {
      el.hidden = true;
    });
    continueButton.hidden = true;
    successBox.hidden = false;
    dialog.setAttribute('aria-labelledby', 'ttj-pincode-success-title');
    dialog.setAttribute('aria-describedby', 'ttj-pincode-success-text');
    successButton.focus();
  }

  function setLoading(isLoading) {
    loading = isLoading;
    root.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
    input.readOnly = isLoading; // keep the pincode visible but stable while checking
    if (isLoading) {
      dialog.setAttribute('aria-busy', 'true');
    } else {
      dialog.removeAttribute('aria-busy');
    }
  }

  function focusables() {
    return Array.prototype.filter.call(
      dialog.querySelectorAll('input, button'),
      function (el) {
        return !el.disabled && !el.hidden && !el.closest('[hidden]');
      }
    );
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      // No dismissal in this version; also stop other theme handlers from reacting.
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onFocusIn(event) {
    if (!root.contains(event.target)) input.focus();
  }

  function open() {
    if (openedOnce || !root.hidden) return; // one popup only, never duplicated
    if (core.getSavedPincode() || wasDismissed()) return;
    openedOnce = true;
    root.hidden = false;

    // Everything else on the page is inert (no clicks, no keyboard, hidden from assistive tech) while open.
    // Inert does not stop the page from scrolling, and no scroll lock is applied.
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === root || el.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(el.tagName)) return;
      el.inert = true;
      inerted.push(el);
    });

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('focusin', onFocusIn, true);
    window.requestAnimationFrame(function () {
      root.classList.add('is-open');
      input.focus();
    });
  }

  function close() {
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('focusin', onFocusIn, true);
    inerted.forEach(function (el) {
      el.inert = false;
    });
    inerted = [];
    root.classList.remove('is-open');
    root.hidden = true;
  }

  // Used by both "Continue Shopping for Jewellery" buttons: closes and remembers it for the rest of the session.
  function dismiss() {
    markDismissed();
    close();
  }

  input.addEventListener('input', function () {
    if (!loading) clearMessage();
  });

  continueButton.addEventListener('click', dismiss);
  successButton.addEventListener('click', dismiss);

  form.addEventListener('submit', async function (event) {
    event.preventDefault();
    if (loading) return; // ignore repeated clicks / Enter while a request is running

    var pincode = input.value.trim();
    if (!core.isValidPincode(pincode)) {
      continueButton.hidden = true;
      show('error', core.MESSAGES.invalid);
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }

    clearMessage();
    setLoading(true);
    try {
      var data = await core.fetchAvailability(pincode);
      core.savePincode(pincode); // reuse the existing session cookie
      core.announceResult(pincode, data); // a product-page checker shows this without another request
      if (data.available === true) {
        showSuccess(); // popup + backdrop stay until the visitor clicks Continue Browsing
      } else {
        show('unavailable', core.MESSAGES.unavailable);
        continueButton.hidden = false;
      }
    } catch (error) {
      show('error', core.MESSAGES.error);
    } finally {
      setLoading(false);
    }
  });

  // ---- Trigger ----------------------------------------------------------------------------------------------

  var main = document.querySelector('main[data-template]');
  var isHomepage = !!main && main.getAttribute('data-template') === 'index';

  if (!isHomepage) {
    open(); // collection / product / other pages: show on page open, no delay
  } else {
    var target = document.querySelector(MOST_LOVED_SELECTOR);
    var userScrolled = window.scrollY > 0;
    var inView = false;
    var observer = null;

    var stopWatching = function () {
      if (observer) observer.disconnect();
      observer = null;
      window.removeEventListener('scroll', onScroll);
    };

    var tryOpen = function () {
      if (inView && userScrolled) {
        stopWatching();
        open();
      }
    };

    // Not opened just because the section is already visible on load (e.g. restored scroll position on a tall
    // screen): the visitor must actually scroll first.
    var onScroll = function () {
      userScrolled = true;
      if (!target) {
        // Fallback if the section was removed from the homepage: open after the visitor scrolls one screen.
        if (window.scrollY >= window.innerHeight) {
          stopWatching();
          open();
        }
        return;
      }
      tryOpen();
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    if (target && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
          });
          tryOpen();
        },
        { threshold: 0.1 }
      );
      observer.observe(target);
    } else {
      target = null; // no section / no observer support: use the scroll-distance fallback above
    }
  }

  // Back/Forward cache: a restored page keeps its JS state, so no listeners are re-added. Just re-check state.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    if (core.getSavedPincode() || wasDismissed()) {
      if (!root.hidden && !root.querySelector('.ttj-pincode-modal__success:not([hidden])')) close();
      return;
    }
    if (!isHomepage) open();
  });
})();
